import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Radio,
  Tag,
  Tooltip,
} from 'antd';
import { debounce, filter, forEach, isEmpty, map } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as FilterIcon } from '../../../../../assets/svg/sliders-solid.svg';

import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PAYMENT_TYPE,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  PLAN_NAMES,
  PLAN_NAMES_OPTIONS,
  PLAN_TYPES,
  PLAN_TYPES_OPTIONS,
  PLAY_PLAN_NAME,
  ROUTES,
  SORT,
  SORTING,
  STATUS,
  USER_FORM_STATUS,
  USER_LIBRARY_TYPE,
  USER_ROLES,
} from '../../../../../common/constants';
import {
  formatDate,
  getPlayPlanLabel,
  hasNonEmptyValueObj,
  hasPermission,
} from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import SearchComponent from '../../../../../components/SearchComponent';
import SortDropdown from '../../../../../components/SortDropdown';
import useInfiniteScroll from '../../../../../hooks/useInfiniteScroll';
import { LIBRARIES } from '../../../../../modules/orders/modules/new/graphql/Queries';
import { USERS_BY_LIBRARY } from '../graphql/Queries';
import AllUserList from './AllUserList';
import AllUsersFilterModal from './AllUsersFilterModal';
import CombinedNewOrderTable from './NewOrderTable';
import UserReturnTable from './ReturnTable';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
  search: '',
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

const initialUsersFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
};

const initialUsersSort = {
  field: 'latestOrderCreatedAt',
  order: 'DESC',
};

const roleFilter = [USER_ROLES?.USER?.key];
let filterScrollDebounceJob = null;

const EndUsersTable = ({ exportFilter, setExportFilter, userId }) => {
  const {
    state: {
      isDesktop,
      currentUser,
      permissions,
      commonPermissions: { isLibraryPermission },
    },
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const history = useHistory();
  const [endUserList, setEndUserList] = useState([]);
  const [endUserSearchTerm, setEndUserSearchTerm] = useState('');
  const [isEmptyEndUserList, setIsEmptyEndUserList] = useState(false);
  const [endUser, setEndUser] = useState({});
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isActivePrompts, setIsActivePrompts] = useState(false);
  const [isEndUserLoading, setIsEndUserLoading] = useState(true);
  const [isActiveLoading, setIsActiveLoading] = useState(false);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isFetchMoreOrderLoading, setIsFetchMoreOrderLoading] = useState(false);

  const [usersByLibrary] = useLazyQuery(USERS_BY_LIBRARY, {
    onCompleted: (response) => {
      if (isDesktop) {
        setEndUserList([...response?.usersByLibrary?.data]);
      } else {
        setEndUserList([...endUserList, ...response?.usersByLibrary?.data]);
      }
      if (
        response?.usersByLibrary?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyEndUserList(true);
      } else {
        setIsEmptyEndUserList(false);
      }
      setIsFetchMoreOrderLoading(false);

      if (response?.usersByLibrary?.data?.length < LIMIT) {
        setIsActive(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.usersByLibrary?.count,
      };
      setPaginationProp(pagination);
      setIsEndUserLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [libraries, { loading: isLibrariesLoading }] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      if (response?.libraries?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.libraries?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.libraries?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.id }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsEndUserLoading(true);
    usersByLibrary({
      variables: {
        libraryId: currentUser?.libraryId,
        filter: {
          ...initialUsersFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: endUserSearchTerm,
        },
        sort: initialUsersSort,
      },
    });
  }, []);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsEndUserLoading(true);
    setSortedInfo(sorter);
    usersByLibrary({
      variables: {
        libraryId: currentUser?.libraryId,
        filter: {
          ...initialUsersFilter,
          skip,
          limit: pagination?.pageSize,
          search: endUserSearchTerm,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          isActive: finalWhereFilters?.status,
          types:
            finalWhereFilters?.billingCycle?.length > 0
              ? finalWhereFilters?.billingCycle
              : undefined,
          names:
            finalWhereFilters?.planSubscribed?.length > 0
              ? finalWhereFilters?.planSubscribed
              : undefined,
          libraryIds: finalWhereFilters?.name,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialUsersSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setEndUserSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsEndUserLoading(true);
    setEndUserList([]);
    usersByLibrary({
      variables: {
        libraryId: currentUser?.libraryId,
        filter: {
          ...initialUsersFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          isActive: finalWhereFilters?.status,
          types:
            finalWhereFilters?.billingCycle?.length > 0
              ? finalWhereFilters?.billingCycle
              : undefined,
          names:
            finalWhereFilters?.planSubscribed?.length > 0
              ? finalWhereFilters?.planSubscribed
              : undefined,
          libraryIds: finalWhereFilters?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialUsersSort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const getFilterData = (confirm) => {
    setIsEndUserLoading(true);
    usersByLibrary({
      variables: {
        libraryId: currentUser?.libraryId,
        filter: {
          ...initialUsersFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: endUserSearchTerm,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          isActive:
            whereFilter?.status === true || whereFilter?.status === false
              ? whereFilter?.status
              : undefined,
          types:
            whereFilter?.billingCycle?.length > 0
              ? whereFilter?.billingCycle
              : undefined,
          names:
            whereFilter?.planSubscribed?.length > 0
              ? whereFilter?.planSubscribed
              : undefined,
          libraryIds: whereFilter?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialUsersSort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    let whereFiltersCopy;
    if (dataIndex === 'status') {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: null,
      };
    } else {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: [],
      };
    }
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsEndUserLoading(true);
    usersByLibrary({
      variables: {
        libraryId: currentUser?.libraryId,
        filter: {
          ...initialUsersFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: endUserSearchTerm,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          isActive:
            whereFiltersCopy?.status === true ||
            whereFiltersCopy?.status === false
              ? whereFiltersCopy?.status
              : undefined,
          types:
            whereFiltersCopy?.billingCycle?.length > 0
              ? whereFiltersCopy?.billingCycle
              : undefined,
          names:
            whereFiltersCopy?.planSubscribed?.length > 0
              ? whereFiltersCopy?.planSubscribed
              : undefined,
          libraryIds: whereFiltersCopy?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialUsersSort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      const optionsCopy = [];

      switch (filterIndex) {
        case 'name':
          libraries({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'status':
          forEach(STATUS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'billingCycle':
          forEach(PLAN_TYPES_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'planSubscribed':
          forEach(PLAN_NAMES_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const handleFilter = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsAllFilterModalOpen(false);
    const filters = {
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      billingCycle: values?.billingCycle,
      name: values?.name,
      planSubscribed: values?.planSubscribed,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);

    usersByLibrary({
      variables: {
        libraryId: currentUser?.libraryId,
        filter: {
          ...initialUsersFilter,
          search: endUserSearchTerm,
          dateRange: {
            from: filters?.createdAt?.from?.$d,
            to: filters?.createdAt?.to?.$d,
          },
          isActive: filters?.status,
          types:
            filters?.billingCycle?.length > 0
              ? filters?.billingCycle
              : undefined,
          names:
            filters?.planSubscribed?.length > 0
              ? filters?.planSubscribed
              : undefined,
          libraryIds: filters?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialUsersSort,
      },
    });
  };

  const onFilterScroll = (event, dataIndex) => {
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (!isLibrariesLoading && !filterIsEnd) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'name':
              libraries({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'name':
        libraries({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const handleOrderScroll = () => {
    if (!isFetchMoreOrderLoading && !isEndUserLoading) {
      const currentLength = endUserList?.length;
      setIsFetchMoreOrderLoading(true);
      usersByLibrary({
        variables: {
          libraryId: currentUser?.libraryId,
          filter: {
            ...initialUsersFilter,
            skip: currentLength,
            limit: paginationProp?.pageSize || LIMIT,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialUsersSort,
        },
      });
    }
  };
  const infiniteScrollRef = useInfiniteScroll(handleOrderScroll, isActive);

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: value,
    };
    if (!value?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'status') {
        return (
          <div className="custom-filter-dropdown">
            <Group
              options={USER_FORM_STATUS}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}

            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ||
      finalWhereFilters?.[dataIndex] === true ||
      finalWhereFilters?.[dataIndex] === false ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'USER NAME',
      dataIndex: 'firstName',
      key: 'firstName',
      ellipsis: true,
      sorter: true,
      width: 250,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'firstName' && sortedInfo?.order,
      render: (_, record) => (
        <>
          {record?.firstName || record?.lastName ? (
            <div className="table-data-with-id">{`${
              record?.firstName ? record?.firstName : ''
            } ${record?.lastName ? record?.lastName : ''}`}</div>
          ) : (
            '-'
          )}
          {record?.userNumber ? (
            <div className="font-size-12">({record?.userNumber})</div>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      width: 250,
      sorter: true,
      align: 'left',
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'email' && sortedInfo?.order,
      render: (_, record) => record?.email || '-',
    },
    {
      title: 'CONTACT',
      dataIndex: 'contactNo',
      key: 'contactNo',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.contactNo || '-',
    },
    {
      title: 'BILLING CYCLE',
      dataIndex: 'billingCycle',
      key: 'billingCycle',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        PLAN_TYPES?.[record?.subscription?.subscriptionPlan?.type]?.key || '-',
      ...filterPopup('billingCycle', false),
    },
    {
      title: 'PLAN SUBSCRIBED',
      dataIndex: 'planSubscribed',
      key: 'planSubscribed',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        record?.subscription?.subscriptionPlan?.name?.includes(PLAY_PLAN_NAME)
          ? getPlayPlanLabel(record?.subscription?.subscriptionPlan)
          : PLAN_NAMES?.[record?.subscription?.subscriptionPlan?.name]?.key ||
            '-',
      ...filterPopup('planSubscribed', false),
    },
    {
      title: 'PRIMARY MAPPED LIBRARY NAME',
      dataIndex: 'mappedLibraryName',
      key: 'mappedLibraryName',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      onCell: (record) => {
        const primaryLibrary = record?.userLibraries?.find(
          (lib) => lib?.type === USER_LIBRARY_TYPE?.PRIMARY?.value,
        );
        const { id, libraryNumber } = primaryLibrary?.library || {};
        return {
          onClick: (e) => {
            e?.stopPropagation();
            if (isLibraryPermission && (record?.pincode?.library?.id || id)) {
              history?.push(
                `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${
                  ROUTES?.BASIC_DETAILS
                }/view/${id || record?.pincode?.library?.id}/${
                  libraryNumber || record?.pincode?.library?.libraryNumber
                }`,
              );
            }
          },
          className: `${
            isLibraryPermission &&
            (record?.pincode?.library?.id || id) &&
            'pointer table-cell'
          }`,
        };
      },
      render: (_, record) => {
        const primaryLibrary = record?.userLibraries?.find(
          (lib) => lib?.type === USER_LIBRARY_TYPE?.PRIMARY?.value,
        );
        const { name, libraryNumber } = primaryLibrary?.library || {};
        return (
          <>
            <div className="table-data-with-id">
              {name || record?.pincode?.library?.name}
            </div>
            <div className="font-size-12">
              {libraryNumber || record?.pincode?.library?.libraryNumber
                ? `(${
                    libraryNumber || record?.pincode?.library?.libraryNumber
                  })`
                : '-'}
            </div>
          </>
        );
      },
      ...filterPopup('name'),
    },
    {
      title: 'ADDITIONAL MAPPED LIBRARY NAME',
      dataIndex: 'additionalMappedLibraryName',
      key: 'additionalMappedLibraryName',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      onCell: (record) => {
        const additionalLibrary = record?.userLibraries?.find(
          (lib) => lib?.type === USER_LIBRARY_TYPE?.ADDITIONAL?.value,
        );
        return {
          onClick: (e) => {
            e?.stopPropagation();
            if (isLibraryPermission && additionalLibrary?.library?.id) {
              history?.push(
                `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${additionalLibrary?.library?.id}/${additionalLibrary?.library?.libraryNumber}`,
              );
            }
          },
          className: `${
            isLibraryPermission &&
            additionalLibrary?.library?.id &&
            'pointer table-cell'
          }`,
        };
      },
      render: (_, record) => {
        const additionalLibrary = record?.userLibraries?.find(
          (lib) => lib?.type === USER_LIBRARY_TYPE?.ADDITIONAL?.value,
        );
        const { name, libraryNumber } = additionalLibrary?.library || {};
        return (
          <>
            {name ? (
              <>
                <div className="table-data-with-id">{name}</div>
                <div className="font-size-12">({libraryNumber})</div>
              </>
            ) : (
              '-'
            )}
          </>
        );
      },
    },
    {
      title: 'USER SINCE',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'ACCOUNT DETAILS',
      dataIndex: 'accountDetails',
      key: 'accountDetails',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column table-tooltip-cell',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, record) => {
        const bankAccounts = record?.bankAccounts?.filter(
          (account) => account?.isDefault,
        );
        const allBankAccounts = bankAccounts?.map((bankAccount) => {
          if (bankAccount?.type === PAYMENT_TYPE?.ACCOUNT) {
            return `(Name. ${bankAccount?.holderName}) (No. ${bankAccount?.accountNumber}) (IFSC: ${bankAccount?.ifsc})`;
          }
          return ` (UPI ID: ${bankAccount.upiId}) `;
        });
        return (
          <>
            {bankAccounts?.length > 0 ? (
              <Tooltip
                title={allBankAccounts}
                placement="topLeft"
                trigger="click"
                className="pointer"
              >
                {allBankAccounts}
              </Tooltip>
            ) : (
              '-'
            )}
          </>
        );
      },
    },
    {
      title: 'LAT',
      dataIndex: 'lat',
      key: 'lat',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.latLong?.coordinates?.[1] || '-',
    },
    {
      title: 'LNG',
      dataIndex: 'lng',
      key: 'lng',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.latLong?.coordinates?.[0] || '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={
            record?.isActive ? STATUS?.ACTIVE?.color : STATUS?.INACTIVE?.color
          }
          text={
            record?.isActive ? STATUS?.ACTIVE?.label : STATUS?.INACTIVE?.label
          }
        />
      ),
      ...filterPopup('status'),
    },
  ]?.filter((item) => {
    if (item?.dataIndex === 'actions' && !hasUpdatePermission) {
      return item?.hidden;
    }
    return !item?.hidden;
  });

  const locale = {
    emptyText: isEmptyEndUserList ? '' : <span />,
  };

  const additionalLibrary = endUser?.userLibraries?.find(
    (lib) => lib?.type === USER_LIBRARY_TYPE?.ADDITIONAL?.value,
  );

  const expandedRowRender = (data) => (
    <>
      <h2>New Orders</h2>
      <CombinedNewOrderTable userId={selectedUserId || userId} />
      <h2>Return Orders</h2>
      <UserReturnTable userId={selectedUserId || userId} />
    </>
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpand = async (expanded, record) => {
    if (expanded) {
      setSelectedUserId(record?.id);
      setExpandedRowKeys([...expandedRowKeys, record?.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys?.filter((key) => key !== record?.id));
    }
  };

  const sort = [
    {
      label: SORTING?.USER_NAME_A_Z?.label,
      value: SORTING?.USER_NAME_A_Z?.value,
      field: SORTING?.USER_NAME_A_Z?.field,
      sort: SORTING?.USER_NAME_A_Z?.sort,
    },
    {
      label: SORTING?.USER_NAME_Z_A?.label,
      value: SORTING?.USER_NAME_Z_A?.value,
      field: SORTING?.USER_NAME_Z_A?.field,
      sort: SORTING?.USER_NAME_Z_A?.sort,
    },

    {
      label: SORTING?.USER_EMAIL_A_Z?.label,
      value: SORTING?.USER_EMAIL_A_Z?.value,
      field: SORTING?.USER_EMAIL_A_Z?.field,
      sort: SORTING?.USER_EMAIL_A_Z?.sort,
    },
    {
      label: SORTING?.USER_EMAIL_Z_A?.label,
      value: SORTING?.USER_EMAIL_Z_A?.value,
      field: SORTING?.USER_EMAIL_Z_A?.field,
      sort: SORTING?.USER_EMAIL_Z_A?.sort,
    },
  ];
  const [isAllSortPopoverOpen, setIsAllSortPopoverOpen] = useState(false);

  const handleSort = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsEndUserLoading(true);
    setIsAllSortPopoverOpen(false);
    setIsActive(true);
    setEndUserList([]);
    const filteredSort = sort?.filter((item) => item?.value === values?.sort);
    if (filteredSort?.[0]?.field) {
      setSortedInfo({
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      });
    }
    usersByLibrary({
      variables: {
        libraryId: currentUser?.libraryId,
        filter: {
          ...initialUsersFilter,
          search: endUserSearchTerm,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          isActive: finalWhereFilters?.status,
          types:
            finalWhereFilters?.billingCycle?.length > 0
              ? finalWhereFilters?.billingCycle
              : undefined,
          names:
            finalWhereFilters?.planSubscribed?.length > 0
              ? finalWhereFilters?.planSubscribed
              : undefined,
          libraryIds: finalWhereFilters?.name,
        },
        sort: filteredSort?.[0]
          ? {
              field: filteredSort?.[0]?.field,
              order: filteredSort?.[0]?.sort,
            }
          : initialUsersSort,
      },
    });
  };

  const expandIcon = ({ expanded, onExpand, record }) =>
    expanded ? (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded disabled"
        aria-label="Expand row"
        aria-expanded="false"
        onClick={(e) => {
          e?.stopPropagation();
          onExpand(record, e);
        }}
        // disabled={isDisabled}
      />
    ) : (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
        aria-label="Collapse row"
        aria-expanded="true"
        onClick={(e) => {
          e?.stopPropagation();
          onExpand(record, e);
        }}
        // disabled={isDisabled}
      />
    );

  return (
    <>
      <div className="d-flex justify-end mb-16 flex-wrap">
        {!isDesktop && (
          <div className="d-flex justify-end align-center">
            <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
              <FilterIcon
                className="mobile-svg-icon ml-16 mr-16"
                onClick={() => setIsAllFilterModalOpen(true)}
              />
            </Badge>
            <div className="mr-16 d-flex align-center">
              <SortDropdown
                isOpen={isAllSortPopoverOpen}
                setIsOpen={setIsAllSortPopoverOpen}
                options={map(sort, (item) => item)}
                sortedInfo={sortedInfo}
                setSortedInfo={setSortedInfo}
                onFinish={handleSort}
              />
            </div>
          </div>
        )}
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>

      {isDesktop && (
        <CommonTable
          locale={locale}
          columns={columns}
          data={endUserList || []}
          loading={isEndUserLoading}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          rowKey={(record) => record?.id}
          rowClassName="pointer table-cell"
          expandable={{
            onExpand: (expanded, record) => handleExpand(expanded, record),
            expandedRowRender: (record) => expandedRowRender(record),
            expandIcon,
            expandRowByClick: true,
          }}
        />
      )}

      {!isDesktop && (
        <div className="mobile-container-search-action no-scroll-bar">
          <AllUsersFilterModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
          />

          <AllUserList
            userList={endUserList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreOrderLoading={isFetchMoreOrderLoading}
            isEmptyOrderList={isEmptyEndUserList}
            isEndUserLoading={isEndUserLoading}
          />
        </div>
      )}
    </>
  );
};

export default EndUsersTable;
